/** Shared content shapes for the shell components. */

export interface SocialLink {
  /** Flaticon class suffix, e.g. `facebook` for `<i class="flaticon-facebook">`. */
  icon: string
  href: string
  label?: string
}

/**
 * Where a link goes. Exactly one of the two:
 *
 * - `to` — an in-app route, rendered as a router `Link`.
 * - `href` — an absolute URL: another KKF app on its own subdomain, or an external site,
 *   rendered as a plain `<a>`.
 *
 * Both are optional so one shape covers both cases; `SmartLink` is what resolves it, and
 * it is the only place in the system that should have to know the difference.
 */
export interface LinkTarget {
  to?: string
  href?: string
}

export type NavLink = LinkTarget & { label: string }

export interface NavGroup {
  label: string
  children: NavLink[]
}

/**
 * One rich card in a mega panel: a photograph, a name and a line of detail.
 *
 * It is a card rather than a link because the thing it points at is a product, and a
 * visitor deciding between two products from a nav panel wants to see them. A list of
 * names makes them open both pages to find out which is which.
 */
export interface NavMegaItem extends LinkTarget {
  /** The mono line above the name — what kind of thing this is. */
  meta?: string
  title: string
  descr: string
  image: string
  imageAlt: string
}

/**
 * A nav entry that opens a panel of cards rather than a list of links.
 *
 * `NavGroup` is the right shape for six link names in three columns. This is for the case
 * where the panel has to *show* what it is offering — which for a lineup of two products
 * means the panel is the shop's own front page in miniature, ending in a way through to
 * all of it.
 */
export interface NavMega {
  label: string
  items: NavMegaItem[]
  /** The button that closes the panel — "View all products". */
  cta: NavLink
}

export type NavEntry = NavLink | NavGroup | NavMega

export const isNavGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry
export const isNavMega = (entry: NavEntry): entry is NavMega => 'items' in entry
